import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { naira, compactNaira } from "../../lib/format";
import { HC, label } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Field, Input, Select, Empty } from "./ui";
import { toast } from "sonner";
import {
  Plus, Calculator, ShieldCheck, Send, Check, X, FileText, Landmark, CheckCircle2,
  AlertTriangle, Lock, Download,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { downloadReport } from "../accounting/exportHelpers";

const FLOW = ["draft", "calculated", "validated", "pending_approval", "approved", "payslips_generated", "posted", "completed"];

export default function Payroll() {
  const { locationId, locations } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [scopeLoc, setScopeLoc] = useState("");
  const [reject, setReject] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/hr/payroll/runs").then(setRuns).catch((e) => toast.error(formatApiError(e)));
  const openRun = async (id) => { try { setRun(await api.get(`/hr/payroll/runs/${id}`)); } catch (e) { toast.error(formatApiError(e)); } };

  useEffect(() => { load(); }, []);
  useEffect(() => { const r = params.get("run"); if (r) { openRun(r); setParams({}); } /* eslint-disable-next-line */ }, [params]);

  const act = async (path, body, msg) => {
    setBusy(true);
    try {
      const res = await api.post(`/hr/payroll/runs/${run.id}/${path}`, body || {});
      toast.success(msg);
      if (path === "post-to-accounting") toast.success(`Posted as journal ${res.journal_number}`);
      await openRun(run.id); load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const create = async () => {
    try {
      const r = await api.post("/hr/payroll/runs", { period, location_id: scopeLoc || null, scope: scopeLoc ? "location" : "company" });
      toast.success(`Payroll run ${r.number} created`); setNewOpen(false); load(); openRun(r.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const errors = (run?.exceptions || []).filter((e) => e.severity === "error");
  const warnings = (run?.exceptions || []).filter((e) => e.severity !== "error");
  const t = run?.totals || {};

  return (
    <div className="space-y-4" data-testid="hr-payroll-page">
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <SectionHead title="Payroll runs" sub="Deterministic, rule-based calculation — AI never computes payroll" />
          <div className="flex-1" />
          <Btn onClick={() => setNewOpen(true)} data-testid="hr-new-payroll-run"><Plus size={15} /> New Payroll Run</Btn>
        </div>
        <Table
          testid="hr-payroll-runs-table" rows={runs} empty="No payroll runs yet" onRowClick={(r) => openRun(r.id)}
          columns={[
            { key: "number", label: "RUN", render: (r) => <span className="font-semibold">{r.number}</span> },
            { key: "period", label: "PERIOD" },
            { key: "location_name", label: "SCOPE" },
            { key: "employees", label: "EMPLOYEES", align: "right", render: (r) => r.totals?.employees ?? 0 },
            { key: "gross", label: "GROSS", align: "right", render: (r) => <span className="tabular-nums">{compactNaira(r.totals?.gross)}</span> },
            { key: "net", label: "NET", align: "right", render: (r) => <span className="tabular-nums">{compactNaira(r.totals?.net)}</span> },
            { key: "exceptions", label: "EXCEPTIONS", align: "right", render: (r) => (r.exceptions?.length || 0) },
            { key: "status", label: "STATUS", render: (r) => <Pill status={r.status} /> },
          ]}
        />
      </Card>

      {/* RUN DETAIL */}
      <Dialog open={!!run} onOpenChange={(o) => !o && setRun(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="hr-payroll-detail">
          {run && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {run.number} · {run.period}
                  <Pill status={run.status} testid="hr-run-status" />
                  {run.locked && <Lock size={14} style={{ color: HC.soft }} />}
                </DialogTitle>
                <DialogDescription>
                  {run.location_name} · {run.date_from} → {run.date_to}
                  {run.journal_number ? ` · Posted to Layer 7 as ${run.journal_number}` : ""}
                </DialogDescription>
              </DialogHeader>

              {/* workflow */}
              <div className="flex flex-wrap items-center gap-1.5">
                {FLOW.map((s) => {
                  const done = FLOW.indexOf(run.status) >= FLOW.indexOf(s) && run.status !== "rejected";
                  return (
                    <span key={s} className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[10.5px] font-bold tracking-wide"
                      style={{ background: done ? HC.greenTint : HC.borderSoft, color: done ? HC.green : HC.soft }}>
                      {done && <CheckCircle2 size={11} />}{label(s)}
                    </span>
                  );
                })}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
                {[["Employees", t.employees ?? 0], ["Gross", naira(t.gross)], ["Overtime", naira(t.overtime)],
                  ["PAYE", naira(t.paye)], ["Pension (EE)", naira(t.pension_employee)],
                  ["Deductions", naira(t.deductions)], ["Net", naira(t.net)]].map(([k, v]) => (
                  <div key={k} className="rounded-xl border p-3" style={{ borderColor: HC.border }}>
                    <div className="text-[9.5px] font-bold tracking-[0.14em]" style={{ color: HC.soft }}>{k.toUpperCase()}</div>
                    <div className="text-[13.5px] font-semibold tabular-nums mt-0.5" style={{ color: HC.text }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* actions */}
              <div className="flex flex-wrap gap-2">
                <Btn variant="outline" onClick={() => act("calculate", null, "Payroll calculated")} disabled={busy || run.locked}
                  data-testid="run-calculate"><Calculator size={15} /> Calculate</Btn>
                <Btn variant="outline" onClick={() => act("validate", null, "Validation complete")} disabled={busy}
                  data-testid="run-validate"><ShieldCheck size={15} /> Validate</Btn>
                <Btn variant="outline" onClick={() => act("submit", null, "Submitted for approval")} disabled={busy}
                  data-testid="run-submit"><Send size={15} /> Submit for approval</Btn>
                <Btn onClick={() => act("approve", null, "Payroll approved")} disabled={busy || run.status !== "pending_approval"}
                  data-testid="run-approve"><Check size={15} /> Approve</Btn>
                <Btn variant="danger" onClick={() => setReject(true)} disabled={busy || run.status !== "pending_approval"}
                  data-testid="run-reject"><X size={15} /> Reject</Btn>
                <Btn variant="outline" onClick={() => act("generate-payslips", null, "Payslips generated")} disabled={busy}
                  data-testid="run-payslips"><FileText size={15} /> Generate payslips</Btn>
                <Btn variant="gold" onClick={() => act("post-to-accounting", null, "Posted to accounting")} disabled={busy}
                  data-testid="run-post"><Landmark size={15} /> Post to Accounting</Btn>
                <Btn variant="outline" onClick={() => act("complete", null, "Payroll completed")} disabled={busy}
                  data-testid="run-complete"><CheckCircle2 size={15} /> Complete</Btn>
              </div>

              {/* exceptions */}
              <Card className="!p-4" testid="hr-run-exceptions">
                <SectionHead title={`Exceptions (${run.exceptions?.length || 0})`}
                  sub={errors.length ? `${errors.length} blocking issue(s) must be resolved before approval` : "No blocking issues"} />
                {run.exceptions?.length ? (
                  <div className="space-y-1.5 max-h-52 overflow-y-auto">
                    {[...errors, ...warnings].map((e, i) => (
                      <div key={i} className="flex items-start gap-2 text-[12.5px] p-2 rounded-lg"
                        style={{ background: e.severity === "error" ? HC.redTint : HC.amberTint }}>
                        <AlertTriangle size={14} className="mt-0.5 shrink-0" style={{ color: e.severity === "error" ? HC.red : HC.amber }} />
                        <span style={{ color: HC.text }}><b>{label(e.code)}</b> — {e.message}</span>
                      </div>
                    ))}
                  </div>
                ) : <Empty text="No exceptions raised" />}
              </Card>

              {/* payslips */}
              <Card className="!p-4">
                <SectionHead title={`Payslips (${run.payslips?.length || 0})`} sub="Per-employee earnings and deductions" />
                <Table
                  testid="hr-run-payslips" rows={(run.payslips || []).slice(0, 200)} empty="Calculate the payroll to generate payslips"
                  columns={[
                    { key: "employee_name", label: "EMPLOYEE", render: (r) => (
                        <span><span className="block font-semibold">{r.employee_name}</span>
                          <span className="block text-[11px]" style={{ color: HC.soft }}>{r.employee_no}</span></span>) },
                    { key: "department_name", label: "DEPARTMENT" },
                    { key: "location_name", label: "LOCATION" },
                    { key: "gross", label: "GROSS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.gross)}</span> },
                    { key: "overtime_hours", label: "OT (H)", align: "right" },
                    { key: "paye", label: "PAYE", align: "right", render: (r) => <span className="tabular-nums">{naira(r.paye)}</span> },
                    { key: "total_deductions", label: "DEDUCTIONS", align: "right", render: (r) => <span className="tabular-nums">{naira(r.total_deductions)}</span> },
                    { key: "net", label: "NET", align: "right", render: (r) => <span className="tabular-nums font-semibold">{naira(r.net)}</span> },
                    { key: "pdf", label: "", align: "right", render: (r) => (
                        <Btn variant="outline" className="h-8 !px-2.5" data-testid={`slip-pdf-${r.id}`}
                          onClick={() => downloadReport(`/hr/payslips/${r.id}/pdf`, `payslip-${r.employee_no}-${r.period}.pdf`)}>
                          <Download size={13} />
                        </Btn>) },
                  ]}
                />
              </Card>

              {!!run.approvals?.length && (
                <div className="rounded-xl border p-3" style={{ borderColor: HC.border }}>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>APPROVAL TRAIL</div>
                  {run.approvals.map((a, i) => (
                    <div key={i} className="text-[12px]" style={{ color: HC.muted }}>
                      {new Date(a.at).toLocaleString("en-NG")} · {a.user_name} — {a.decision}{a.reason ? ` (${a.reason})` : ""}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New payroll run</DialogTitle>
            <DialogDescription>Monthly payroll for the whole company or a single location.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Field label="Period (YYYY-MM)"><Input data-testid="run-period" value={period} onChange={(e) => setPeriod(e.target.value)} /></Field>
            <Field label="Scope">
              <Select data-testid="run-scope" value={scopeLoc} onChange={(e) => setScopeLoc(e.target.value)}>
                <option value="">Full company</option>
                {(locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </Select>
            </Field>
          </div>
          <DialogFooter>
            <Btn variant="outline" onClick={() => setNewOpen(false)}>Cancel</Btn>
            <Btn onClick={create} data-testid="run-create">Create run</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reject} onOpenChange={setReject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject payroll</DialogTitle>
            <DialogDescription>A reason is mandatory and stored in the audit trail.</DialogDescription>
          </DialogHeader>
          <Field label="Reason"><Input data-testid="run-reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
          <DialogFooter>
            <Btn variant="outline" onClick={() => setReject(false)}>Cancel</Btn>
            <Btn onClick={async () => {
              if (!reason.trim()) return toast.error("A rejection reason is required");
              await act("reject", { reason }, "Payroll rejected"); setReject(false); setReason("");
            }} data-testid="run-confirm-reject">Reject payroll</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
